import type { VerlaufEintrag } from '@nac2s/shared'
import wasserzeichen from '@nac2s/ui/marke/veeky-schriftzug.png'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Knopf } from '@/teile/Knopf'
import { Leer } from '@/teile/Tabelle'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import { ladeSipgateZugang } from '../api/einstellungen.js'
import { ladeVerlauf } from '../api/verlauf.js'
import { useSitzung } from '../session/SitzungContext.js'
import { Anrufliste } from './Verlauf.js'

/**
 * Die Startseite sagt dem Anwender **eines**: ob er telefonieren kann.
 *
 * Keine Rechte, keine Kennungen, keine Zahlen — das ist Sache des Partners.
 * Hier steht gross und in der Farbe der Lage, wie es steht; ist etwas zu
 * tun, steht daneben die eine Schaltfläche, die es erledigt.
 */
type Lage =
  | { status: 'laedt' }
  | {
      status: 'bekannt'
      ton: 'gut' | 'wartet' | 'stoerung'
      titel: string
      satz: string
      /** Was zu tun ist — `null`, wenn nichts. */
      handlung: { beschriftung: string; ziel: string } | null
    }

const FARBE = {
  gut: 'text-fg-success-primary',
  wartet: 'text-fg-warning-primary',
  stoerung: 'text-fg-error-primary',
} as const

const ZU_DEN_EINSTELLUNGEN = { beschriftung: 'Einstellungen öffnen', ziel: '/einstellungen' }

export function Uebersicht() {
  const { zustand } = useSitzung()
  const navigate = useNavigate()
  const [lage, setLage] = useState<Lage>({ status: 'laedt' })
  const [letzte, setLetzte] = useState<VerlaufEintrag[] | null>(null)

  useEffect(() => {
    let aktiv = true
    ladeVerlauf(5)
      .then((v) => {
        if (aktiv) setLetzte(v.eintraege)
      })
      .catch(() => {
        if (aktiv) setLetzte([])
      })
    return () => {
      aktiv = false
    }
  }, [])

  useEffect(() => {
    let aktiv = true
    void (async () => {
      try {
        const zugang = await ladeSipgateZugang()
        if (!aktiv) return
        if (!zugang.hinterlegt) {
          setLage({
            status: 'bekannt',
            ton: 'wartet',
            titel: 'Telefon noch nicht verbunden',
            satz: 'Hinterlegen Sie einmalig Ihren sipgate-Zugang, dann können Sie wählen.',
            handlung: ZU_DEN_EINSTELLUNGEN,
          })
        } else if (zugang.status === 'invalid') {
          setLage({
            status: 'bekannt',
            ton: 'stoerung',
            titel: 'Zugang ungültig',
            satz: 'sipgate nimmt Ihren Token nicht mehr an. Bitte einen neuen hinterlegen.',
            handlung: ZU_DEN_EINSTELLUNGEN,
          })
        } else if (!zugang.ausgewaehltesGeraet) {
          setLage({
            status: 'bekannt',
            ton: 'wartet',
            titel: 'Telefon noch nicht gewählt',
            satz: 'Ihr Zugang ist da — es fehlt noch, über welches Telefon gewählt wird.',
            handlung: ZU_DEN_EINSTELLUNGEN,
          })
        } else {
          setLage({
            status: 'bekannt',
            ton: 'gut',
            titel: 'Alles in Ordnung',
            satz: 'Alle Funktionen bereit.',
            handlung: null,
          })
        }
      } catch (error) {
        if (!aktiv) return
        setLage({
          status: 'bekannt',
          ton: 'stoerung',
          titel: 'Dienst nicht erreichbar',
          satz:
            error instanceof ApiError
              ? error.message
              : 'Der Dienst hat nicht geantwortet. Bitte später erneut versuchen.',
          handlung: null,
        })
      }
    })()
    return () => {
      aktiv = false
    }
  }, [])

  if (zustand.status !== 'angemeldet') return null

  return (
    <div>
      {/* Derselbe Kopf wie die Kundenliste der Kundenverwaltung: Schimmer aus
          der Ecke, Marke als Wasserzeichen dahinter — statt der Zahl steht
          hier die Lage, in ihrer Farbe. */}
      <div className="relative overflow-hidden px-6 pt-6 pb-[18px]">
        <div
          aria-hidden="true"
          className="schimmer pointer-events-none absolute -top-[13rem] -right-[6rem] size-[25rem]"
        />
        <img
          src={wasserzeichen}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-1/2 hidden w-[20rem] -translate-x-1/2 -translate-y-1/2 opacity-[0.08] select-none lg:block"
        />
        <div className="relative">
          <p className="text-[13px] text-quaternary">{heute()}</p>
          <p className="mt-4 font-display text-[22px] leading-tight font-bold tracking-[-0.01em] text-primary">
            {begruessung()}, {zustand.sitzung.anzeigename}
          </p>
          {lage.status === 'laedt' ? (
            <p
              role="status"
              className="mt-3 font-display text-[2.6rem] leading-[0.95] font-extrabold tracking-[-0.03em] text-quaternary"
            >
              Wird geprüft …
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap items-end gap-x-8 gap-y-4">
              <div className="max-w-xl">
                <p
                  role="status"
                  className={cx(
                    'font-display text-[2.6rem] leading-[0.95] font-extrabold tracking-[-0.03em] text-balance',
                    FARBE[lage.ton],
                  )}
                >
                  {lage.titel}
                </p>
                <p className="mt-3 text-sm text-tertiary">{lage.satz}</p>
              </div>
              {lage.handlung ? (
                <Knopf stark onClick={() => void navigate(lage.handlung?.ziel ?? '/')}>
                  {lage.handlung.beschriftung}
                </Knopf>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 px-6 pb-8">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-base font-bold text-primary">Letzte Anrufe</h2>
          <Link to="/verlauf" className="text-[13px] text-tertiary hover:text-primary">
            Ganzer Verlauf
          </Link>
        </div>
        {letzte && letzte.length > 0 ? (
          <Anrufliste eintraege={letzte} />
        ) : (
          <Leer titel="Noch keine Anrufe">
            Sobald Sie über den Hotkey wählen, stehen die letzten Anrufe hier.
          </Leer>
        )}
      </div>
    </div>
  )
}

/** Die Anrede folgt der Uhrzeit des Rechners — der Anwender sitzt davor. */
function begruessung(jetzt = new Date()): string {
  const stunde = jetzt.getHours()
  if (stunde >= 5 && stunde < 11) return 'Guten Morgen'
  if (stunde >= 18 || stunde < 5) return 'Guten Abend'
  return 'Hallo'
}

function heute(): string {
  return new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })
}
